import { useCallback, useEffect, useRef, useState } from "react";
import { LanderConfigAction, useLanderConfig } from "../commons/ConfigContext";
import { EventType, FetchStatus, System } from "../commons/Constants";
import { ExperimentIds } from "../commons/constants/experiment";
import { isControlCohort } from "../experimentation/helper";
import {
  fetchRollerAds,
  RollerAd,
  RollerAdsFetchResult,
} from "../rollerads/rollerAdsFeed";
import { sendRollerAdsEvent } from "../rollerads/rollerAdsEvent";
import { sendYahooEvent, ErrorCode } from "./yahooEvent";
import { trackClick } from "../commons/signals";
import Logger from "../commons/Logger";

export type BackfillState = "idle" | "pending" | "failed";

export const BACKFILL_CONTROL_TAG = "PW_YAHOO_BACKFILL_CONTROL";
export const BACKFILL_TREATMENT_TAG = "PW_YAHOO_BACKFILL_TREATMENT";

function isValidRedirectUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}

function handleAdRedirect(
  ad: RollerAd,
  landerConfig: any
): "redirected" | "failed" {
  if (!isValidRedirectUrl(ad.url)) {
    sendRollerAdsEvent(EventType.ERROR, landerConfig, {
      errorCode: "ERROR_ROLLERADS_INVALID_URL",
      isBackfill: true,
    });
    Logger.error("RollerAds backfill invalid redirect URL:", ad.url);
    return "failed";
  }

  trackClick({ section: "ads", widget: "ad_redirect" });
  sendRollerAdsEvent(EventType.AD_REDIRECT, landerConfig, {
    bid: ad.bid,
    campaignId: ad.campaign_id,
    adUrl: ad.url,
    isBackfill: true,
  });

  window.location.href = ad.url;
  return "redirected";
}

interface BackfillInput {
  fetchState: number;
  xmlResult: Document | null;
  isAdClicked: boolean;
}

function shouldTriggerBackfill(
  fetchState: number,
  xmlResult: Document | null
): boolean {
  if (fetchState === FetchStatus.FAILURE) return true;
  if (fetchState !== FetchStatus.SUCCESS) return false;
  const adCount =
    xmlResult?.getElementById("adListings")?.getElementsByTagName("Listing")
      ?.length ?? 0;
  return adCount === 0;
}

export function useYahooBackfillRollerAds(input: BackfillInput) {
  const { fetchState, xmlResult, isAdClicked } = input;
  const { landerConfig, landerConfigDispatch, mergedConfig } =
    useLanderConfig();
  const { experiment: { key, cohort } = {} } = landerConfig;
  const hasTriggered = useRef(false);
  const [backfillState, setBackfillState] = useState<BackfillState>("idle");

  const inExperiment = key === ExperimentIds.yahooBackfillRollerAds;
  const inTreatmentCohort =
    inExperiment && !!cohort && !isControlCohort(cohort);
  const isParkWeb = landerConfig.system === System.PARKWEB;
  const isEligible = isParkWeb && inTreatmentCohort;

  let typeTag: string | undefined = landerConfig.yahoo?.typeTag;
  if (inExperiment && isParkWeb) {
    typeTag = inTreatmentCohort ? BACKFILL_TREATMENT_TAG : BACKFILL_CONTROL_TAG;
  }

  useEffect(() => {
    if (!typeTag) return;

    landerConfigDispatch({
      type: LanderConfigAction.UPDATE_YAHOO_TYPE_TAG,
      payload: { typeTag },
    });
  }, [typeTag, landerConfigDispatch]);

  const triggerBackfill = useCallback(
    async (searchKeyword?: string): Promise<"redirected" | "failed"> => {
      if (!isEligible || hasTriggered.current) return "failed";
      hasTriggered.current = true;

      sendYahooEvent(EventType.BACKFILL_TRIGGER, landerConfig);

      const keywords: string[] = [];
      if (searchKeyword) keywords.push(searchKeyword);
      if (mergedConfig?.keywords?.length) {
        keywords.push(...mergedConfig.keywords);
      }

      let result: RollerAdsFetchResult;
      try {
        result = await fetchRollerAds({
          rootDomain: landerConfig.domain.rootDomain,
          customerId: landerConfig.customerId,
          keywords: keywords.length > 0 ? keywords : void 0,
          backfill: true,
        });
      } catch (error) {
        Logger.error("RollerAds backfill fetch error:", error);
        sendRollerAdsEvent(EventType.ERROR, landerConfig, {
          errorCode: ErrorCode.ERROR_ROLLERADS_FETCH,
          isBackfill: true,
        });
        return "failed";
      }

      if (result.status === 200 && result.ads.length > 0) {
        return handleAdRedirect(result.ads[0], landerConfig);
      }

      const errorCode =
        result.status === 0
          ? "ERROR_ROLLERADS_BLOCKED"
          : "ERROR_ROLLERADS_NO_ADS";
      sendRollerAdsEvent(EventType.ERROR, landerConfig, {
        errorCode,
        isBackfill: true,
      });
      return "failed";
    },
    [isEligible, landerConfig, mergedConfig?.keywords]
  );

  useEffect(() => {
    if (!isAdClicked || !isEligible || hasTriggered.current) return;
    if (fetchState === FetchStatus.PENDING) return;
    if (fetchState === FetchStatus.SUCCESS && !xmlResult) return;
    if (!shouldTriggerBackfill(fetchState, xmlResult)) return;

    if (fetchState === FetchStatus.SUCCESS) {
      sendYahooEvent(EventType.ERROR, landerConfig, {
        errorCode: ErrorCode.ERROR_YAHOO_NO_ADS,
      });
    }

    const searchKeyword =
      xmlResult
        ?.getElementsByTagName("MetaData")[0]
        ?.getAttribute("keywords") || void 0;

    setBackfillState("pending");
    triggerBackfill(searchKeyword).then((outcome) => {
      if (outcome === "failed") setBackfillState("failed");
    });
  }, [
    isAdClicked,
    isEligible,
    fetchState,
    xmlResult,
    landerConfig,
    triggerBackfill,
  ]);

  return { isEligible, backfillState, triggerBackfill, typeTag };
}
